import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/axios';
import { API_ENDPOINTS } from '../config/api';
import { registerForPushIfPossible, warmUpPushServiceWorker } from '../services/webPushService';
import {
  clearAuthStorage,
  clearLegacyLocalAuth,
  consumeImpersonateTokenFromUrl,
  getAuthToken,
  getUserInfoRaw,
  hasAuthToken,
  setAuthToken,
  setUserInfoRaw,
} from '../utils/authStorage';
import { logoutFcheros } from '../utils/fcherosSession';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const DEFAULT_IDLE_TIMEOUT_MINUTES = 60;
const LOGIN_FAILURE_LOCK_NOTICE = '연속 5회 실패 시 1일 동안 로그인할 수 없습니다.';

const addLoginFailureLockNotice = (message) => {
  if (!message || message.includes(LOGIN_FAILURE_LOCK_NOTICE)) {
    return message;
  }
  if (/\d+회\s*로그인\s*실패/.test(message)) {
    return `${message}\n${LOGIN_FAILURE_LOCK_NOTICE}`;
  }
  return message;
};

export const AuthProvider = ({ children }) => {
  // sessionStorage에서 초기 사용자 정보 복원 (같은 탭 새로고침 시 즉시 사용 가능)
  const getInitialUser = () => {
    clearLegacyLocalAuth();
    // window.open 새 창은 opener sessionStorage를 복제할 수 있음 → 대리 로그인 hash 토큰 최우선
    if (consumeImpersonateTokenFromUrl()) {
      return null;
    }
    try {
      const userInfo = getUserInfoRaw();
      if (userInfo) {
        return JSON.parse(userInfo);
      }
    } catch (error) {
      console.error('사용자 정보 복원 실패:', error);
      clearAuthStorage();
    }
    return null;
  };

  const [user, setUser] = useState(getInitialUser);
  const [loading, setLoading] = useState(true);
  const [idleTimeoutSuppressed, setIdleTimeoutSuppressed] = useState(false);
  const idleTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const idleTimeoutMinutesRef = useRef(DEFAULT_IDLE_TIMEOUT_MINUTES);
  const fetchUserProfileInFlightRef = useRef(null);

  const fetchUserProfile = useCallback(async () => {
    if (fetchUserProfileInFlightRef.current) {
      return fetchUserProfileInFlightRef.current;
    }
    const request = (async () => {
      try {
        const response = await api.get(API_ENDPOINTS.USER_PROFILE || '/api/users/profile');
        if (response && response.data) {
          setUser(response.data);
          setUserInfoRaw(JSON.stringify(response.data));
          return response.data;
        }
      } catch (error) {
        const status = error.response?.status;
        const detail = error.response?.data?.error || error.response?.data?.message || error.message;
        console.error('사용자 프로필 조회 실패:', status ?? '(네트워크/URL)', detail);
        clearAuthStorage();
      }
      return null;
    })();
    fetchUserProfileInFlightRef.current = request;
    try {
      return await request;
    } finally {
      fetchUserProfileInFlightRef.current = null;
    }
  }, []);

  useEffect(() => {
    const initializeUser = async () => {
      const token = getAuthToken();
      if (token) {
        const profile = await fetchUserProfile();
        if (!profile) {
          clearAuthStorage();
          setUser(null);
        }
      } else {
        clearAuthStorage();
        setUser(null);
      }
      setLoading(false);
    };

    initializeUser();
  }, [fetchUserProfile]);

  // 로그인·세션 복원·계정 전환: SW warm-up + 이미 허용된 경우만 토큰 등록
  useEffect(() => {
    if (!user?.id || !hasAuthToken()) {
      return;
    }
    void warmUpPushServiceWorker();
    void registerForPushIfPossible();
  }, [user?.id]);

  // 백엔드에서 idle 타임아웃(분) 조회
  const fetchAuthConfig = useCallback(async () => {
    try {
      const response = await api.get(API_ENDPOINTS.AUTH_CONFIG || '/api/auth/config');
      if (response?.data?.idleTimeoutMinutes != null && response.data.idleTimeoutMinutes > 0) {
        idleTimeoutMinutesRef.current = response.data.idleTimeoutMinutes;
      }
    } catch (err) {
      // 실패 시 기본값 유지
    }
  }, []);

  const logout = useCallback(async () => {
    // 모바일과 동일: 로그아웃해도 FCM 토큰은 서버에 유지 → 알림 수신 계속.
    // 다른 계정 로그인 시 registerForPushIfPossible() PUT으로 토큰 소유만 갱신됨.
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    await logoutFcheros();
    clearAuthStorage();
    setUser(null);
  }, []);

  // 로그인 상태일 때만: idle 타임아웃 적용 (백엔드 설정 60분 등). idleTimeoutSuppressed 시 비활성.
  useEffect(() => {
    if (!user || idleTimeoutSuppressed) {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return undefined;
    }

    fetchAuthConfig();

    const scheduleIdleLogout = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      const timeoutMs = idleTimeoutMinutesRef.current * 60 * 1000;
      idleTimerRef.current = setTimeout(() => {
        void logout();
      }, timeoutMs);
    };

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      scheduleIdleLogout();
    };

    scheduleIdleLogout();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach((ev) => window.addEventListener(ev, onActivity));

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
    };
  }, [user?.id, logout, fetchAuthConfig, idleTimeoutSuppressed]);

  const login = async (email, password, options = {}) => {
    try {
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();
      // 접속단말기 유형 감지
      const deviceType = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        ? 'MOBILE'
        : 'PCWEB';
      const endpoint = options.useTestLogin ? API_ENDPOINTS.TEST_LOGIN : API_ENDPOINTS.LOGIN;

      const response = await api.post(endpoint, {
        email: trimmedEmail,
        password: trimmedPassword,
        deviceType
      });

      const { token } = response.data;

      setAuthToken(token);
      await fetchUserProfile();
      return { success: true };
    } catch (error) {
      let errorMessage = error.response?.data?.error || error.response?.data?.message || '로그인에 실패했습니다.';

      // "자격 증명에 실패하였습니다" 같은 메시지를 "로그인 실패"로 변경
      if (errorMessage.includes('자격 증명') || errorMessage.includes('이메일 또는 비밀번호')) {
        // 백엔드에서 실패 카운트가 포함된 메시지를 보내지 않으면 기본 메시지 사용
        errorMessage = '로그인에 실패했습니다.';
      }
      errorMessage = addLoginFailureLockNotice(errorMessage);

      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post(API_ENDPOINTS.REGISTER, userData);

      if (response.data.message) {
        return { success: true, message: response.data.message };
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || '회원가입에 실패했습니다.'
      };
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    setUserInfoRaw(JSON.stringify(updatedUser));
  };

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    fetchUserProfile,
    loading,
    setIdleTimeoutSuppressed,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
